import { PROJECTION_ACK_SCHEMA, projectionReceipt } from '../contracts/projectionObserver.contract.js';
import { PROJECTION_OBSERVER_POLICY } from '../policies/projectionObserver.policy.js';

/** Bounded receipt transport. Server acceptance remains the only success authority. */
export function createProjectionReceiptQueue({ clock, current, send, policy = PROJECTION_OBSERVER_POLICY, longTasks = null }) {
  const pending = new Map(), flights = new Set();
  let timer = null, disposed = false, epoch = 0, windowStart = -Infinity, starts = 0;
  const metrics = { acceptedHandler: 0, acceptedRender: 0, expired: 0, rejected: 0, capacity: 0 };
  const retryMetrics = { retryNoReply: 0, retryBusy: 0, retryRate: 0, retryNotEmitted: 0, retryHandlerRequired: 0 };
  const refusalMetrics = { rejectNoReply: 0, rejectBusy: 0, rejectRate: 0, rejectNotEmitted: 0,
    rejectHandlerRequired: 0, rejectStaleMembership: 0, rejectUnavailableOrInvalid: 0,
    rejectOther: 0, rejectedHandler: 0, rejectedRender: 0 };
  const timingMetrics = { appliedToPaintSamples: 0, appliedToPaintMaxMs: 0,
    websocketReceipts: 0, pollingReceipts: 0, unknownTransportReceipts: 0 };
  for (const stage of ['handler', 'render']) for (const suffix of
    ['Samples', 'MaxMs', 'QueueMsAtMax', 'WorkMsAtMax', 'PairedAtMax', 'TimingMissing', 'ServerQueueMaxMs', 'ServerWorkMaxMs'])
    timingMetrics[stage + 'Rpc' + suffix] = 0;
  const countTiming = key => { timingMetrics[key] = Math.min(65535, timingMetrics[key] + 1); };
  const recordTiming = (stage, attempt, result) => {
    const finished = clock.now(), elapsed = finished - attempt.started;
    if (!Number.isFinite(elapsed) || elapsed < 0 || elapsed > policy.deadlineMs) return;
    const prefix = stage === 'handler_applied' ? 'handlerRpc' : 'renderRpc';
    const timing = result.timing, rpcMs = Math.round(elapsed);
    const paired = timing && Object.getPrototypeOf(timing) === Object.prototype
      && Object.keys(timing).length === 2 && ['queueMs', 'workMs'].every(key =>
        Number.isSafeInteger(timing[key]) && timing[key] >= 0 && timing[key] <= policy.deadlineMs)
      && timing.queueMs + timing.workMs <= rpcMs + 2;
    countTiming(prefix + 'Samples');
    if (!paired) countTiming(prefix + 'TimingMissing');
    else for (const [name, key] of [['ServerQueueMaxMs', 'queueMs'], ['ServerWorkMaxMs', 'workMs']])
      timingMetrics[prefix + name] = Math.max(timingMetrics[prefix + name], timing[key]);
    if (timingMetrics[prefix + 'Samples'] === 1 || rpcMs > timingMetrics[prefix + 'MaxMs']) {
      timingMetrics[prefix + 'MaxMs'] = rpcMs;
      timingMetrics[prefix + 'QueueMsAtMax'] = paired ? timing.queueMs : 0;
      timingMetrics[prefix + 'WorkMsAtMax'] = paired ? timing.workMs : 0;
      timingMetrics[prefix + 'PairedAtMax'] = Number(!!paired);
      longTasks?.track(prefix, attempt.started, finished);
    }
    countTiming(attempt.transport === 'websocket' ? 'websocketReceipts'
      : attempt.transport === 'polling' ? 'pollingReceipts' : 'unknownTransportReceipts');
  };
  const refused = (stage, result) => {
    const reason = result?.reason;
    const key = !result ? 'rejectNoReply' : reason === 'busy_or_closed' ? 'rejectBusy'
      : reason === 'socket_rate_limit' ? 'rejectRate' : reason === 'not_emitted' ? 'rejectNotEmitted'
      : reason === 'handler_required' ? 'rejectHandlerRequired' : reason === 'stale_membership' ? 'rejectStaleMembership'
      : reason === 'unavailable_or_invalid' ? 'rejectUnavailableOrInvalid' : 'rejectOther';
    for (const counter of [key, stage === 'handler_applied' ? 'rejectedHandler' : 'rejectedRender'])
      refusalMetrics[counter] = Math.min(65535, refusalMetrics[counter] + 1);
  };
  const retry = key => { retryMetrics[key] = Math.min(65535, retryMetrics[key] + 1); };
  const valid = entry => !disposed && entry.epoch === epoch && current(entry)
    && clock.now() < entry.expires && pending.get(entry.target.envelope) === entry;
  const retire = entry => pending.delete(entry.target.envelope);
  const clearTimer = () => { if (timer !== null) clock.cancel(timer); timer = null; };
  const arm = ms => {
    clearTimer();
    timer = clock.delay(() => { timer = null; pump(); }, Math.max(1, ms));
  };
  const stageState = () => ({ accepted: false, flight: false, started: false, retries: 0, next: 0, needsHandler: false });
  const pump = () => {
    clearTimer();
    if (disposed) return;
    const now = clock.now();
    for (const entry of pending.values()) if (!valid(entry)) {
      if (now >= entry.expires) metrics.expired++;
      retire(entry);
    }
    if (!pending.size) return;
    const expiry = Math.min(...[...pending.values()].map(entry => entry.expires));
    if (flights.size >= policy.maxInFlight) { arm(expiry - now); return; }
    if (now - windowStart >= 1000) { windowStart = now; starts = 0; }
    if (starts >= policy.maxRequestsPerSecond) { arm(Math.min(expiry - now, windowStart + 1000 - now)); return; }
    const candidates = [];
    for (const entry of pending.values()) {
      for (const stage of ['handler_applied', 'render_observed']) {
        const state = entry.stages[stage];
        if (state.accepted || state.flight) continue;
        if (stage === 'render_observed' && (!entry.painted || !entry.stages.handler_applied.started
          || state.needsHandler && !entry.stages.handler_applied.accepted)) continue;
        candidates.push({ entry, stage, state });
      }
    }
    const candidate = candidates.find(item => item.state.next <= now);
    if (!candidate) { arm(Math.min(expiry - now, ...candidates.map(item => item.state.next - now))); return; }
    const { entry, stage, state } = candidate;
    const attempt = { entry, stage, started: clock.now(), transport: entry.owner.socket?.io?.engine?.transport?.name };
    flights.add(attempt); state.flight = true; state.started = true; starts++;
    let answered = false;
    const reply = result => {
      if (answered) return;
      answered = true; flights.delete(attempt); state.flight = false;
      if (valid(entry)) {
        if (result?.status === true && result.trusted === true && result.stage === stage) {
          recordTiming(stage, attempt, result);
          state.accepted = true;
          if (stage === 'handler_applied') metrics.acceptedHandler++;
          else metrics.acceptedRender++;
          if (entry.stages.handler_applied.accepted && entry.stages.render_observed.accepted) retire(entry);
        } else if (stage === 'render_observed' && result?.reason === 'handler_required'
            && state.retries < policy.maxStageRetries) {
          // A server-side scheduling race never becomes a fabricated handler.
          retry('retryHandlerRequired');
          state.retries++; state.needsHandler = true; state.next = clock.now();
        } else if ((!result || ['not_emitted', 'busy_or_closed', 'socket_rate_limit'].includes(result.reason))
            && state.retries < policy.maxStageRetries) {
          retry(!result ? 'retryNoReply' : result.reason === 'busy_or_closed' ? 'retryBusy'
            : result.reason === 'socket_rate_limit' ? 'retryRate' : 'retryNotEmitted');
          state.retries++; state.next = clock.now() + policy.retryMs;
        } else { metrics.rejected++; refused(stage, result); retire(entry); }
      }
      pump();
    };
    // Paint may pipeline behind the sent handler, without awaiting an extra RTT.
    // Both stages still need separate trusted server responses; cancellation
    // retains occupied RPC slots until the port's finite callback timeout.
    try { send(entry.owner, { ackSchema: PROJECTION_ACK_SCHEMA, room: entry.publicRoom,
      receipt: projectionReceipt(entry.target, stage) }, reply); }
    catch { reply(null); }
    if (!answered) pump();
  };
  return Object.freeze({
    add(owner, target, publicRoom, expires) {
      pump();
      if (disposed || pending.has(target.envelope)) return null;
      if (!Number.isFinite(expires) || expires <= clock.now() || expires - clock.now() > policy.deadlineMs) {
        metrics.expired++; return null;
      }
      const orphans = new Set([...flights].filter(attempt => !pending.has(attempt.entry.target.envelope)).map(attempt => attempt.entry));
      if (pending.size + orphans.size >= policy.maxPending) { metrics.capacity++; return null; }
      const entry = { owner, target, publicRoom, epoch, expires, painted: false, appliedAt: clock.now(),
        stages: { handler_applied: stageState(), render_observed: stageState() } };
      pending.set(target.envelope, entry); pump();
      return entry;
    },
    paint(entry) { if (valid(entry)) {
      const elapsed = clock.now() - entry.appliedAt;
      if (!entry.painted && Number.isFinite(elapsed) && elapsed >= 0 && elapsed <= policy.deadlineMs) {
        countTiming('appliedToPaintSamples');
        timingMetrics.appliedToPaintMaxMs = Math.max(timingMetrics.appliedToPaintMaxMs, Math.round(elapsed));
      }
      entry.painted = true; pump();
    } },
    has: entry => valid(entry),
    sync: pump,
    cancel() { epoch++; clearTimer(); pending.clear(); },
    close() { disposed = true; epoch++; clearTimer(); pending.clear(); },
    inspect: () => ({ ...metrics, pending: pending.size, flight: flights.size > 0, timer: timer !== null }),
    diagnostics: () => Object.fromEntries(Object.entries({ ...retryMetrics, ...refusalMetrics, ...timingMetrics, ...metrics, pending: pending.size, flights: flights.size })
      .map(([key, value]) => [key, Math.min(65535, value)])),
  });
}
